/**
 * One decoding pass, on top of the one the router already applied.
 *
 * A `+` means a space, and is turned into one before the escapes are read so
 * that an escaped plus — `%2B` — stays a plus rather than becoming a space in
 * its turn.
 *
 * Each run of valid `%XX` escapes is decoded on its own. A `%` that does not
 * start one is left as it stands, so a segment mixing the two (`caf%C3%A9`
 * beside `100%`) keeps the percent and still recovers the accent. Decoding
 * the whole segment at once throws on the stray `%` and drops the accent.
 */
const PERCENT_ESCAPE = /(?:%[0-9a-fA-F]{2})+/g;

const decoded = (segment: string): string =>
  segment.replaceAll('+', ' ').replaceAll(PERCENT_ESCAPE, (run) => {
    try {
      return decodeURIComponent(run);
    } catch {
      // A well-formed escape that is not valid UTF-8. A string here cannot
      // hold the bytes, so the escape stays as written rather than being
      // replaced with a different character.
      return run;
    }
  });

/**
 * A gateway front door's path segment, as the phrase it names.
 *
 * `/illustrations/business-man-on-platform` searches for "business man on
 * platform". The segment is the phrase with its spaces written as dashes, so
 * recovering it turns them back.
 *
 * Lossy by design. A phrase that genuinely contained a dash comes back with a
 * space, so `president-elect` recovers as "president elect". Preserving the
 * dash would resolve a different search than the page runs.
 *
 * The segment arrives already decoded once, by the router that matched it,
 * and is decoded once more. Only a slug carrying an escaped `%` can tell the
 * two passes apart.
 */
const phraseFromSegment = (segment: string): string =>
  decoded(segment).replaceAll('-', ' ').trim();

/**
 * The parameters a matched route implies but cannot declare.
 *
 * `staticParams` covers what a path *states* — `/search/2/film` is film — but
 * a gateway category names its phrase in a dynamic segment, so the value has
 * to be computed from the match rather than written beside the route.
 *
 * Keyed on the segment name. `:category` and `:videocategory` belong to the
 * gateway routes — no other mounted route uses them; the editorial paths are
 * splats. A route that reused the name would be claiming the same meaning.
 *
 * Returned separately from `params` rather than merged into it so the caller
 * keeps the precedence it wants. These are overrides, applied over the query
 * string. A segment that recovers to nothing is still an override, so a
 * query-string phrase does not survive a segment that names none.
 */
export const parametersFromRoute = (
  params: Readonly<Record<string, string | undefined>>
): Readonly<Record<string, string>> => {
  const category = params.category ?? params.videocategory;
  if (!category) return {};

  return { phrase: phraseFromSegment(category) };
};
